import React, { CSSProperties, useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import axios from "axios";
import moment from "moment-timezone";
import CONFIG from "../../../../model/TSConfig";
import { ComponentOnlyLoader } from "../../../../components/Loaders/ComponentOnlyLoader/ComponentOnlyLoader";
import { useDateRangeSelector } from "../../../../redux/actions/app";
import { GRAPH_COLORS } from "../../../../designsystem/KC_Card/constants";
import { tt } from "../../../../utils/i18n/i18n";
import KC_Heading4 from "../../../../designsystem/typography/KC-Heading/KC_Heading4";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";

interface DailyData {
  date: string;
  gamesPlayed: number;
  gamesWon: number;
  gamesRedeemed: number;
}

const STYLE: CSSProperties = {
  width: "100%",
  height: "100%",
  overflowX: "auto",
  overflowY: "hidden"
};

export default function DailyStatistics() {
  const dateRange = useDateRangeSelector();
  const atd = useAccountToDisplaySelector();

  const [data, setData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (!atd?.accountToDisplay?.trackingID) {
      return;
    }

    if (!atd?.accountToDisplay?.avgBasket) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${CONFIG.jetpackBackendUrl}/custdash_v2/daily`,
        {
          params: {
            startTs: moment(dateRange.start).unix(),
            endTs: moment(dateRange.end).unix(),
            timezone: moment.tz.guess(),
            isAll: false,
            owner: atd.accountToDisplay?.trackingID
          }
        }
      );

      const formattedData = response.data.payload.data.map((item: any) => ({
        date: moment(item.date).format("DD/MM"),
        gamesPlayed: item.gamesPlayed || 0,
        gamesWon: item.gamesWon || 0,
        gamesRedeemed: item.gamesRedeemed || 0
      }));

      setData(formattedData);
    } catch (error) {
      console.error("Error fetching daily statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [dateRange]);

  return (
    <div
      className="d-flex flex-column mt-5 kc-card-dashboard kc-card-dashboard-with-graph"
      style={{
        height: 550
      }}
    >
      <KC_Heading4 text={tt("activity")} className="mb-4" />
      <ComponentOnlyLoader loading={loading}>
        <div style={STYLE}>
          <div style={{ minWidth: 720, height: 450 }}>
            <ResponsiveContainer width="100%" height={450} debounce={50}>
              <LineChart
                data={data}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0
                }}
              >
                <XAxis
                  dataKey="date"
                  height={60}
                  interval="preserveStartEnd"
                  tick={<CustomXAxisTick />}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="gamesPlayed"
                  stroke={GRAPH_COLORS[0]}
                  name={tt("games_played")}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="gamesWon"
                  stroke={GRAPH_COLORS[1]}
                  name={tt("games_won")}
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="gamesRedeemed"
                  stroke={GRAPH_COLORS[2]}
                  name={tt("games_redeemed")}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </ComponentOnlyLoader>
    </div>
  );
}

const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const maxLength = 5;
  const text =
    payload.value.length > maxLength
      ? `${payload.value.substring(0, maxLength)}...`
      : payload.value;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="end"
        fill="#666"
        transform="rotate(-45)"
        fontSize={12}
      >
        {text}
      </text>
    </g>
  );
};
